import { isEmailValid } from 'utils/email';
import { ChangeEvent, useCallback, useState } from 'react';
import { useAuthenticate } from 'hooks/useAuthenticate/useAuthenticate';
import { useWorkflowStateMachine } from 'hooks/useWorkflowStateMachine';
import { getAuthenticationType } from 'state/brand';
import { useAppSelector } from 'state/store';
import { useCustomNavigate } from 'hooks/useCustomNavigate/useCustomNavigate';

export const useLoginVM = () => {
    const { login, isLoginLoading, error, resetError } = useAuthenticate();
    const { goRedirectOrNext } = useWorkflowStateMachine();
    const { navigateToForgottenPassword } = useCustomNavigate();

    const authenticationType = useAppSelector(getAuthenticationType);
    const shouldDisplayContinueButton = authenticationType === 'Hybrid';

    const [email, setEmail] = useState<string>();
    const [password, setPassword] = useState<string>();

    const handleUpdateEmail = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
        },
        [setEmail],
    );

    const handleUpdatePassword = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setPassword(event.target.value);
        },
        [setPassword],
    );

    const handleForgotPassword = useCallback(() => {
        navigateToForgottenPassword(email);
    }, [email, navigateToForgottenPassword]);

    const areInputsValid = useCallback(
        () => !!password?.length && isEmailValid(email),
        [email, password],
    );

    const checkEmailFormat = useCallback(() => !email || isEmailValid(email), [email]);
    const checkPassword = useCallback(() => !password || password.length > 2, [password]);

    const submit = useCallback(() => {
        if (!email || !password || isLoginLoading) return;
        login({ username: email, password });
    }, [email, password, isLoginLoading, login]);

    const submitContinue = useCallback(() => {
        goRedirectOrNext();
    }, [goRedirectOrNext]);

    return {
        email,
        handleUpdateEmail,
        password,
        handleUpdatePassword,
        isEmailValid: checkEmailFormat(),
        isPasswordValid: checkPassword(),
        areInputsValid: areInputsValid(),
        submit,
        submitContinue,
        shouldDisplayContinueButton,
        error,
        resetError,
        handleForgotPassword,
    };
};
